import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, doc, updateDoc, writeBatch, serverTimestamp, arrayUnion, addDoc, setDoc } from "firebase/firestore";
import { Search, ArrowUpDown, Package, Download, CheckCircle, History, AlertTriangle, QrCode, X, Image as ImageIcon, Printer } from 'lucide-react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { downloadQrSvg, downloadQrPng } from '../../../utils/qrLabel';
import { buildPieceScanCode, parseScanInput } from '../../../lib/scanCode';
import { aggregatePiecesFromSheets, pieceProductionStatus } from '../../../lib/pieces.mjs';

// --- Sub-componentes: Modals (sin cambios) ---
const FullscreenImageModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[61]" onClick={onClose}>
            <div className="relative max-w-full max-h-full h-full w-full flex justify-center items-center p-4" onClick={(e) => e.stopPropagation()}>
                <img src={imageUrl} alt="Vista ampliada" className="max-w-full max-h-full object-contain" />
                <button onClick={onClose} className="absolute top-4 right-4 bg-white text-black rounded-full h-8 w-8 flex items-center justify-center">&times;</button>
            </div>
        </div>
    );
};

const ReportMissingModal = ({ piece, user, db, jobId, onClose }) => {
    const [missingQty, setMissingQty] = useState(1);
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!reason.trim() || missingQty <= 0) {
            alert("Por favor, especifica una cantidad y una razón válidas.");
            return;
        }
        setIsSubmitting(true);
        try {
            const batch = writeBatch(db);
            const historyEntry = { action: 'Reporte de Faltante', user: user.email, timestamp: new Date(), details: `Se reportaron ${missingQty} piezas faltantes. Razón: ${reason}` };
            
            const pieceInventoryRef = doc(db, `jobs/${jobId}/piece_inventory`, piece.code);
            batch.set(pieceInventoryRef, { history: arrayUnion(historyEntry) }, { merge: true });

            const taskData = { jobId: jobId, pieceCode: piece.code, title: `Reproducción requerida: ${piece.code}`, description: `Se solicitaron ${missingQty} piezas de reposición. Causa: ${reason}`, status: 'pending', type: 'system_reproduction', createdBy: user.email, createdAt: serverTimestamp() };
            const tasksCollectionRef = collection(db, 'jobs', jobId, 'tasks');
            batch.set(doc(tasksCollectionRef), taskData);
            
            await batch.commit();
            onClose();
        } catch (error) { console.error("Error reporting missing piece:", error); alert("No se pudo crear la tarea de reproducción.");
        } finally { setIsSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
                <div className="p-4 border-b"><h2 className="text-lg font-bold">Reportar Piezas Faltantes: {piece.code}</h2></div>
                <div className="p-6 space-y-4">
                    <div><label className="font-semibold">Cantidad Faltante</label><input type="number" min="1" value={missingQty} onChange={(e) => setMissingQty(parseInt(e.target.value, 10))} className="w-full p-2 mt-1 border rounded-md"/></div>
                    <div><label className="font-semibold">Razón del Faltante</label><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows="3" placeholder="Ej: Pieza dañada en corte..." className="w-full p-2 mt-1 border rounded-md"></textarea></div>
                </div>
                <div className="flex justify-end p-4 bg-gray-50 space-x-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">Cancelar</button>
                    <button onClick={handleSubmit} disabled={isSubmitting || !reason.trim() || missingQty <= 0} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md disabled:bg-gray-400">Confirmar y Crear Tarea</button>
                </div>
            </div>
        </div>
    );
};

const PieceHistoryModal = ({ pieceCode, history, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex justify-center items-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center"><h2 className="text-lg font-bold">Historial de Auditoría: {pieceCode}</h2><button onClick={onClose}>&times;</button></div>
            <div className="p-6 space-y-3 overflow-y-auto">
                {history && history.length > 0 ? ( [...history].reverse().map((entry, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md border text-sm">
                        <p className="font-semibold">{entry.action}</p>
                        {entry.details && <p className="text-xs text-gray-600 mt-1">{entry.details}</p>}
                        <p className="text-xs text-gray-400 mt-2">{entry.timestamp?.toDate ? new Date(entry.timestamp.toDate()).toLocaleString() : 'Fecha no disponible'} - por {entry.user}</p>
                    </div>
                ))) : <p className="text-gray-500">No hay historial de auditoría para esta pieza.</p>}
            </div>
        </div>
    </div>
);

const QRScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
    useEffect(() => {
        if (!isOpen) return;
        const scanner = new Html5QrcodeScanner('qr-reader', { qrbox: { width: 250, height: 250 }, fps: 10, }, false);
        const handleSuccess = (decodedText, decodedResult) => { scanner.clear(); onScanSuccess(decodedText); };
        const handleError = (error) => {};
        scanner.render(handleSuccess, handleError);
        return () => { if (scanner && scanner.getState() !== 3) { scanner.clear().catch(err => console.error("Failed to clear scanner:", err)); } };
    }, [isOpen, onScanSuccess]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-[60] flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md flex flex-col">
                <div className="p-4 border-b flex justify-between items-center"><h2 className="text-lg font-bold">Escanear Código QR</h2><button onClick={onClose} className="text-gray-500 hover:text-gray-800">&times;</button></div>
                <div id="qr-reader" className="p-4"></div>
            </div>
        </div>
    );
};

const ScannedPieceDetailModal = ({ piece, physicalInfo, onClose }) => {
    if (!piece) return null;
    const status = piece.cutQuantity >= piece.totalQuantity ? { text: 'Producido', style: 'bg-green-100 text-green-800' } : { text: 'En Proceso', style: 'bg-yellow-100 text-yellow-800' };

    return (
         <div className="fixed inset-0 bg-black bg-opacity-70 z-[60] flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
                 <header className="p-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-bold">{piece.code} - {piece.name}</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20}/></button>
                 </header>
                 <main className="p-6 grid grid-cols-2 gap-6">
                    <div className="flex flex-col items-center justify-center">
                       {piece.previewUrl ? <img src={piece.previewUrl} alt="Preview" className="w-full h-auto object-contain rounded-md border"/> : <div className="w-full h-48 bg-gray-100 rounded-md flex items-center justify-center"><ImageIcon className="text-gray-400" size={48}/></div>}
                    </div>
                    <div className="space-y-4">
                        <div><h3 className="font-bold text-lg">Progreso</h3>
                            <div className="text-sm space-y-1 mt-1">
                                <p><strong>Requeridas:</strong> {piece.totalQuantity}</p>
                                <p><strong>Impresas:</strong> {piece.printedQuantity}/{piece.totalQuantity}</p>
                                <p><strong>Cortadas:</strong> {piece.cutQuantity}/{piece.totalQuantity}</p>
                            </div>
                        </div>
                        <div><h3 className="font-bold text-lg">Estado</h3><span className={`mt-1 px-2 py-0.5 inline-flex text-sm font-semibold rounded-full ${status.style}`}>{status.text}</span></div>
                        <div><h3 className="font-bold text-lg">Ubicación en Planchas</h3>
                            <div className="text-xs flex flex-wrap gap-1 mt-1">{Array.from(piece.sheets).map(hri => ( <span key={hri} className="bg-gray-200 px-2 py-0.5 rounded-full">{hri}</span> ))}</div>
                        </div>
                    </div>
                 </main>
            </div>
        </div>
    );
};

const PiecesInventoryTab = ({ db, jobData, user, sheets: allSheets, onSheetSelect, materialsCatalog }) => {
    const [allPieces, setAllPieces] = useState([]);
    const [physicalInventory, setPhysicalInventory] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'code', direction: 'ascending' });
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [reportingModalPiece, setReportingModalPiece] = useState(null);
    const [historyModalPiece, setHistoryModalPiece] = useState(null);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannedPieceDetails, setScannedPieceDetails] = useState(null);
    
    // Identificador del QR: si la pieza está en una sola plancha, usa el nombre de esa plancha
    // (sin #) + código (ej. "1110-COR_001_RTG"); si está en varias, cae a job_código.
    const buildPieceQrLabel = (piece) => {
        const jobNum = String(jobData.id).replace('J-', '');
        const sheetsArr = Array.from(piece.sheets || []).filter(Boolean);
        if (sheetsArr.length === 1) {
            return `${String(sheetsArr[0]).replace(/^#/, '')}_${piece.code}`;
        }
        return `${jobNum}_${piece.code}`;
    };

    // QR vectorial (SVG) con el identificador debajo, para pegar en el arte.
    const handleDownloadQR = async (piece) => {
        if (!piece.code) { alert("La pieza no tiene un código asignado."); return; }
        try {
            const label = buildPieceQrLabel(piece);
            await downloadQrSvg(buildPieceScanCode({ jobId: jobData.id, pieceCode: piece.code }), label, `${label}_QR.svg`);
        } catch (e) { console.error("Error generando QR SVG:", e); alert("No se pudo generar el QR."); }
    };

    // Etiqueta PNG (QR + identificador debajo) lista para impresora térmica de stickers BT genérica.
    const handleDownloadLabel = async (piece) => {
        if (!piece.code) { alert("La pieza no tiene un código asignado."); return; }
        try {
            const label = buildPieceQrLabel(piece);
            await downloadQrPng(buildPieceScanCode({ jobId: jobData.id, pieceCode: piece.code }), label, `${label}_QR.png`);
        } catch (e) { console.error("Error generando etiqueta PNG:", e); alert("No se pudo generar la etiqueta."); }
    };

    useEffect(() => {
        if (!jobData?.id || !allSheets) {
            setIsLoading(false);
            return;
        };

        // Agregado de piezas: lógica PURA compartida (src/lib/pieces.mjs) — la misma
        // que reusa /scan para la búsqueda de pieza y el backfill de pieceCodes (B1b).
        setAllPieces(aggregatePiecesFromSheets(allSheets));
        setIsLoading(false);
        
        const inventoryQuery = collection(db, `jobs/${jobData.id}/piece_inventory`);
        const unsubscribeInventory = onSnapshot(inventoryQuery, (snapshot) => {
            const inventoryMap = {};
            snapshot.forEach(doc => { inventoryMap[doc.id] = { id: doc.id, ...doc.data() }; });
            setPhysicalInventory(inventoryMap);
        });

        return () => { unsubscribeInventory(); };
    }, [db, jobData?.id, allSheets]);
    
    const onScanSuccess = (decodedText) => {
        setIsScannerOpen(false);
        // Contrato único (scanCode.js): acepta el QR v1 y las etiquetas legadas ya impresas.
        const parsed = parseScanInput(decodedText);
        if (!parsed || parsed.kind !== 'piece') {
            alert("El código QR no es una etiqueta de pieza.");
            return;
        }
        if (parsed.jobId !== jobData.id) {
            alert("Código QR no válido o no pertenece a este trabajo.");
            return;
        }
        const piece = allPieces.find(p => p.code === parsed.id);
        if (piece) {
            setScannedPieceDetails(piece);
        } else {
            alert(`Pieza con código "${parsed.id}" no encontrada en el inventario.`);
        }
    };

    const handleSheetClick = (sheetHri) => {
        const sheet = allSheets.find(s => s.hri === sheetHri);
        if (sheet && onSheetSelect) {
            onSheetSelect(sheet);
        }
    };

    const requestSort = (key) => {
        let direction = 'ascending'; if (sortConfig.key === key && sortConfig.direction === 'ascending') { direction = 'descending'; }
        setSortConfig({ key, direction });
    };

    const sortedAndFilteredPieces = useMemo(() => {
        let sortableItems = [...allPieces]; if (searchTerm) { sortableItems = sortableItems.filter(p => p.code.toLowerCase().includes(searchTerm.toLowerCase()) || p.name.toLowerCase().includes(searchTerm.toLowerCase())); }
        sortableItems.sort((a, b) => { const valA = a[sortConfig.key] || ''; const valB = b[sortConfig.key] || ''; if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1; if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1; return 0; });
        return sortableItems;
    }, [allPieces, searchTerm, sortConfig]);
    
    const getPieceRowClass = (piece) => {
        if (piece.cutQuantity >= piece.totalQuantity) { return 'bg-green-100'; }
        if (piece.isPrinting && piece.printedQuantity >= piece.totalQuantity) { return 'bg-yellow-100'; }
        return 'hover:bg-gray-50';
    };

    const getProductionStatus = pieceProductionStatus; // lógica compartida (src/lib/pieces.mjs)

    if (isLoading) { return <div className="p-4 text-center">Calculando inventario de piezas...</div>; }

    return (
        <div className="p-4 bg-white rounded-lg shadow-sm">
            <FullscreenImageModal imageUrl={fullscreenImage} onClose={() => setFullscreenImage(null)} />
            {reportingModalPiece && <ReportMissingModal piece={reportingModalPiece} user={user} db={db} jobId={jobData.id} onClose={() => setReportingModalPiece(null)} />}
            {historyModalPiece && <PieceHistoryModal pieceCode={historyModalPiece.code} history={physicalInventory[historyModalPiece.code]?.history} onClose={() => setHistoryModalPiece(null)} />}
            <QRScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onScanSuccess={onScanSuccess} />
            {scannedPieceDetails && <ScannedPieceDetailModal piece={scannedPieceDetails} physicalInfo={physicalInventory[scannedPieceDetails.code]} onClose={() => setScannedPieceDetails(null)} />}
            
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2"><Package /> Inventario de Piezas del Job</h2>
                <div className="flex items-center gap-4">
                    <button onClick={() => setIsScannerOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-bold rounded-md hover:bg-indigo-700">
                        <QrCode size={18} /> Activar Escáner
                    </button>
                    <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 w-64 border rounded-lg text-sm" /></div>
                </div>
            </div>

            <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Preview</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"><button onClick={() => requestSort('code')} className="flex items-center gap-1">Código <ArrowUpDown size={12} /></button></th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"><button onClick={() => requestSort('name')} className="flex items-center gap-1">Nombre <ArrowUpDown size={12} /></button></th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Planchas</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Auditoría Física</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedAndFilteredPieces.map(piece => {
                            const status = getProductionStatus(piece);
                            const rowClass = getPieceRowClass(piece);
                            const physicalInfo = physicalInventory[piece.code] || { physicalCount: 0, history: [] };
                            return (
                                <tr key={piece.code} className={rowClass}>
                                    <td className="px-4 py-2 text-center"><img src={piece.previewUrl || 'https://placehold.co/64x64/eee/ccc?text=N/A'} alt={`Preview`} className="h-12 w-12 object-contain mx-auto rounded cursor-pointer transition-transform hover:scale-110" onClick={() => setFullscreenImage(piece.previewUrl)} /></td>
                                    <td className="px-4 py-2 font-mono text-sm">{piece.code}</td>
                                    <td className="px-4 py-2 text-sm">{piece.name}</td>
                                    <td className="px-4 py-2"><div className="flex items-center justify-center gap-4"><div className="text-center flex-shrink-0"><span className="font-bold text-lg">{piece.totalQuantity}</span><span className="text-xs text-gray-500 block">Requerido</span></div><div className="text-xs space-y-1 text-left"><div className="flex items-center gap-1 text-purple-700"><CheckCircle size={12}/> Impresas: <span className="font-semibold">{piece.printedQuantity}/{piece.totalQuantity}</span></div><div className="flex items-center gap-1 text-blue-700"><CheckCircle size={12}/> Cortadas: <span className="font-semibold">{piece.cutQuantity}/{piece.totalQuantity}</span></div></div></div></td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                        <div className="flex flex-wrap gap-1 justify-center">
                                            {Array.from(piece.sheets).map(hri => (
                                                <button key={hri} onClick={() => handleSheetClick(hri)} className="bg-gray-200 px-2 py-0.5 rounded-full hover:bg-indigo-200 hover:text-indigo-800 transition-colors">
                                                    {hri}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-center"><span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${status.style}`}>{status.text}</span></td>
                                    <td className="px-4 py-2 text-center"><div className="flex flex-col items-center gap-1"><span className="text-sm">Contadas: <span className="font-bold">{physicalInfo.physicalCount}</span></span><button onClick={() => setReportingModalPiece(piece)} className="text-xs text-red-600 hover:underline flex items-center gap-1"><AlertTriangle size={14}/> Reportar Faltante</button></div></td>
                                    <td className="px-4 py-2 text-center"><div className="flex flex-col items-center gap-1"><button onClick={() => setHistoryModalPiece(piece)} className="p-2 text-gray-400 hover:text-blue-600" title="Ver historial de auditoría"><History size={18} /></button><div className="flex items-center gap-1"><button onClick={() => handleDownloadQR(piece)} className="p-2 text-gray-400 hover:text-blue-600" title="Descargar QR (SVG)" disabled={!piece.code}><Download size={18} /></button><button onClick={() => handleDownloadLabel(piece)} className="p-2 text-gray-400 hover:text-green-600" title="Descargar etiqueta para impresora térmica (PNG: QR + Job + código)" disabled={!piece.code}><Printer size={18} /></button></div></div></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                 {sortedAndFilteredPieces.length === 0 && (<div className="text-center py-10 text-gray-500"><p>No se encontraron piezas en las planchas de este trabajo.</p></div>)}
            </div>
        </div>
    );
};

export default PiecesInventoryTab;
